/**
 * Desktop Wallet Dropdown Menu
 *
 * Anchored popover showing wallet address, token balances, and disconnect
 * button. Positioned below the wallet button in the navbar via Base UI's
 * Popover `anchor` prop.
 */
import {
    ArrowRightLongIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    IndicationUnit,
    Separator
} from "@solomon-labs/design/components/ui";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useEffect, useRef, useState } from "react";

import { useWalletMenuAnalytics } from "../../analytics/useWalletMenuAnalytics";
import { useTokenBalancesData, useWallet } from "../../wallet";
import {
    UsdvMigrationCardPresence,
    UsdvMigrationFlow
} from "../../widgets/usdv-migration";
import { Popover, PopoverClose, PopoverContent } from "../ui/popover";
import { useUnsavedChanges } from "../UnsavedChangesProvider";

import { TokenBalanceList } from "./TokenBalanceList";
import type { WalletMenuProps } from "./types";
import { WalletAddress } from "./WalletAddress";

export function WalletDropdown({
    isOpen,
    onClose,
    anchorEl,
    onBeforeDisconnect,
    usdvMigration,
    renderAccountContent
}: WalletMenuProps): React.ReactElement | null {
    const [copySuccess, setCopySuccess] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const frameRef = useRef<HTMLDivElement>(null);
    const [frameHeight, setFrameHeight] = useState<number>();

    const startMigration = (): void => {
        // Freeze the rendered Account frame, including whichever balance rows it contains.
        if (frameHeight === undefined) {
            const height = frameRef.current?.getBoundingClientRect().height;
            if (height && height > 0) setFrameHeight(height);
        }
        analytics.openMigration();
        setMigrating(true);
    };

    const { publicKey, walletIcon, disconnect } = useWallet();
    const { balances, isLoading, isReconciling } = useTokenBalancesData();
    const { isDirty } = useUnsavedChanges();
    const address = publicKey?.toString();
    const analytics = useWalletMenuAnalytics(
        isOpen,
        address,
        "popover",
        usdvMigration
    );
    useEffect(() => {
        const existing = Object.values(balances).some(
            (balance) => balance !== null
        );
        analytics.balances(
            isLoading && !existing
                ? "initial_loading"
                : (isLoading || isReconciling) && existing
                  ? "refreshing"
                  : "loaded",
            balances.usdv?.gt(0),
            balances.solo?.gt(0)
        );
    });

    // Opening, closing or switching account starts the menu from its root view.
    useEffect(() => {
        setMigrating(false);
        setFrameHeight(undefined);
    }, [isOpen, address]);

    const handleCopy = async (): Promise<void> => {
        if (
            publicKey &&
            (await analytics.copy(() =>
                navigator.clipboard.writeText(publicKey.toString())
            ))
        ) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleDisconnect = async (): Promise<void> => {
        if (await analytics.disconnect(disconnect, onBeforeDisconnect, isDirty))
            onClose();
    };

    if (!publicKey) return null;

    const renderWalletRow = (icon?: React.ReactNode): React.ReactElement => (
        <WalletAddress
            address={publicKey.toString()}
            walletIcon={walletIcon}
            onCopy={handleCopy}
            copySuccess={copySuccess}
            icon={icon}
        />
    );

    const personalContent = (
        <>
            <div className="px-4">
                <TokenBalanceList
                    balances={balances}
                    isLoading={isLoading}
                    isReconciling={isReconciling}
                />
            </div>

            <UsdvMigrationCardPresence
                config={
                    usdvMigration?.stepFlow
                        ? {
                              ...usdvMigration,
                              onMigrate: startMigration
                          }
                        : usdvMigration
                }
                className="px-4"
            />
        </>
    );

    return (
        <Popover
            open={isOpen}
            onOpenChange={(v): void => {
                if (!v) onClose();
            }}
        >
            <PopoverContent
                anchor={anchorEl}
                side="bottom"
                align="end"
                sideOffset={9}
                alignOffset={-4}
                className="w-[336px] bg-surface-raised border border-line-base border-t-2 border-t-accent p-0"
                aria-label="Wallet menu"
            >
                <div
                    ref={frameRef}
                    data-wallet-menu-frame=""
                    className="overflow-y-auto"
                    // A measured height keeps the popup stable across migration steps and Back.
                    style={{ height: frameHeight }}
                >
                    {migrating && usdvMigration?.stepFlow ? (
                        <div className="flex h-full min-h-0 flex-col p-4">
                            <UsdvMigrationFlow
                                onStepViewed={analytics.viewMigrationStep}
                                plan={usdvMigration.plan}
                                coolingEndsAt={usdvMigration.plan.coolingEndsAt}
                                onBack={(reason): void => {
                                    analytics.closeMigration(reason);
                                    setMigrating(false);
                                }}
                                {...usdvMigration.stepFlow}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3 py-4">
                            <div className="flex items-center justify-between px-4">
                                <div className="flex items-start gap-1">
                                    <IndicationUnit
                                        variant="top"
                                        pairWith="h1"
                                    />
                                    <Text
                                        variant="h1"
                                        render={<h1 />}
                                        className="text-foreground-base"
                                    >
                                        Account
                                    </Text>
                                </div>
                                <PopoverClose
                                    onClick={(): void =>
                                        analytics.close("close_button")
                                    }
                                    render={
                                        <ButtonIcon
                                            variant="transparent"
                                            size="xl"
                                            aria-label="Close menu"
                                            icon={<CrossIcon />}
                                        />
                                    }
                                />
                            </div>

                            <div className="px-4">
                                <Separator />
                            </div>

                            {renderAccountContent ? (
                                renderAccountContent(
                                    personalContent,
                                    renderWalletRow
                                )
                            ) : (
                                <>
                                    <div className="px-4">
                                        {renderWalletRow()}
                                    </div>
                                    {personalContent}
                                </>
                            )}

                            <div className="px-4">
                                <Button
                                    variant="raised"
                                    size="lg"
                                    interaction="lifted"
                                    fullWidth
                                    onClick={handleDisconnect}
                                    trailing={
                                        <ArrowRightLongIcon className="w-6 h-6" />
                                    }
                                >
                                    Disconnect
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
